import { Modal } from "antd";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { roles } from "../../constants/commonConstants";
import {
  closeSignInModal,
  signin,
  startDataRefresh,
  resetUserPassword,
} from "../../store";

import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButton";
import showToast from "../../utils/toastNotifications";

const ResetPassword = ({
  isOpen,
  onClose,
  onSuccess,
  onBack,
  isModel = false,
  email,
  otpCode,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const { role } = useSelector((state) => state.auth);

  const [doSignin, isSigninIn, signinError] = useThunk(signin);
  const [doResetPassword, isResettingPassword] = useThunk(resetUserPassword);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      password: "",
      conPassword: "",
    },
    {
      password: true,
      conPassword: true,
    },
    {
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      conPassword: COMMON_FIELD_TYPES.PASSWORD_FIELD,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      conPassword: true,
    });

    const { password, conPassword } = formData;

    // Validate input fields
    if (!password || !conPassword) {
      showToast("error", "Please fill in all fields.");
      return;
    }

    if (password !== conPassword) {
      showToast("error", "Passwords do not match.");
      return;
    }

    if (!validateForm()) {
      showToast("error", "Invalid form data.");
      return;
    }

    const payload = {
      contact: email,
      otp: otpCode,
      newPassword: password,
    };

    const results = await doResetPassword(payload);

    if (results.success) {
      showToast("success", results.message || "Password updated successfully.");
      onClose();
      onSuccess();
    } else {
      showToast(
        "error",
        results.message || "OTP Expired. Please request a new OTP."
      );
      if (onBack) {
        onBack();
      }
      return;
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
      centered
      className="confirmation-modal relative"
      style={{
        backgroundColor: "#F6FCFF",
        borderRadius: "40px",
      }}
    >
      <div className="h-[400px] sm:h-[450px] max-w-[547px] flex flex-col items-center justify-start my-4 sm:my-8">
        <h1 className="text-auth-level-1 font-semibold font-poppins">
          <span className="text-roselea-green">Forgot </span>
          <span className="text-roselea-pink">Password</span>
        </h1>

        <form className="w-full">
          <div className="flex flex-col gap-3 mt-[24px] sm:mt-[40px]">
            <CustomInput
              label=""
              type="password"
              name="password"
              value={formData.password}
              error={touched.password && errors.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onBlur={() => handleBlur("password", formData.password)}
              onFocus={() => clearError("password")}
              placeholder="Enter new password"
              className="w-full h-[54px] px-3 rounded-lg border border-input-border-roselea-green bg-primary-light/30 text-gray-600"
              labelStyle="font-alexandria font-medium  sm:text-[16px] text-dark"
              inputStyle="bg-input-roselea-green bg-opacity-[0.03]"
              isRequired={true}
            />

            <CustomInput
              label=""
              type="password"
              name="conPassword"
              value={formData.conPassword}
              error={touched.conPassword && errors.conPassword}
              onChange={(e) => handleInputChange("conPassword", e.target.value)}
              onBlur={() => handleBlur("conPassword", formData.conPassword)}
              onFocus={() => clearError("conPassword")}
              placeholder="Re-enter new password"
              className="w-full h-[54px] px-3 rounded-lg border border-input-border-roselea-green bg-primary-light/30 text-gray-600"
              labelStyle="font-alexandria font-medium sm:text-[16px] text-dark"
              inputStyle="bg-input-roselea-green bg-opacity-[0.03]"
              isRequired={true}
            />
          </div>

          <div className="absolute bottom-[30px] sm:bottom-[60px] left-0 w-full p-[20px]">
            <CustomButton
              type="submit"
              className="h-[50px] sm:h-[45px] bg-roselea-pink text-roselea-white font-poppins font-semibold text-[16px] leading-[28px] rounded-[12px]"
              buttonText="Update Password"
              onClick={handleSubmit}
              loading={isResettingPassword}
              disabled={isResettingPassword}
            />

            <div className="flex justify-center mt-4">
              <span className="text-sm text-gray-500">
                Back to Sign in?&nbsp;
                <Link
                  to="#"
                  className="font-medium underline text-roselea-pink font-poppins text-[12px] sm:text-[14px] toc-link-forgot-password"
                  onClick={(e) => {
                    e.preventDefault();
                    if (onSuccess) onSuccess();
                  }}
                >
                  Sign in
                </Link>
              </span>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ResetPassword;
